/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
// import { AxiosResponse } from 'axios'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'

import { Day, days } from '@ppwr/poker'

import { getAppVersion } from '../../utils/appVersion'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { events } from '../analytics/analytics-events'
import { shouldSetMeLoading } from './me-loading-gate'
import { useSessionStore } from './useSessionStore'
import {
  QuestionsProgressModel,
  QuestionsProgressModelType,
  QuestionsResponseModel,
  QuestionsResponseModelType,
  selectFromUserInfo,
  selectFromLiveEvent,
  UserInfoModel,
  UserInfoModelType,
  UserModel,
  userModelPrimitives,
  UserModelType,
  userPushNotificationConfigModelPrimitives,
  UserRoles,
  UserStreakResponseModelType
} from '../api-store'
import { UserDetails } from '../api-store/RootStore.base'
import { withRootStore } from '../extensions'
import { LiveEventModelType } from '../monday/monday.store'

const logger = extendLogger(LoggerContexts.SessionUser)
const FIRST_VISIT_MSG = 'Learn poker. Play life differently.'
const SECOND_VISIT_MSG = 'Introducing play streaks – every day is a step towards greatness!'
const VISIT_MSG = [
  'Trust your hand.',
  'Turn bad luck into your next badass move.',
  'Consider every player, but play for yourself.',
  'Set fire to expectations—at the table and in life.',
  'Put your poker face on.',
  'Even the worst cards played well can win.',
  'Strut your bluff.',
  'Trust your hand.',
  'Practice acting on imperfect information.',
  'Betting on yourself is the right kind of risk.',
  'Embrace the power of calculated risks.',
  'Own your decisions, on and off the table.',
  'Play smart, think fierce, win big.',
  'Elevate your game, raise your life.',
  'Turn challenges into opportunities to shine.',
  'Confidence is your ace up the sleeve.',
  'Trust your instincts, make bold moves.',
  'Play bold, conquer the world.',
  'Transform setbacks into comebacks.',
  'Hold your ground, seize the moment.',
  'Strategy is your superpower—use it wisely.',
  'In poker and life, adaptability wins.',
  'Every hand shapes your destiny.',
  'Play with passion, win with grace.',
  'Risk takers change the game.',
  'Thrive on risks, thrive in life.',
  'Embrace uncertainty, unlock new possibilities.',
  'Bold bluffs, big wins – own the table.',
  'In poker and life, adaptability wins.'
]

export type LastVisitedTab = 'home' | 'play' | 'learning'

export const onboardingQuestionsToFilterById = [1]

/**
 * A SessionUserStore model.
 */
export const SessionUserStoreModel = types
  .model('SessionUserStoreModel')
  .props({
    user: types.maybe(UserModel),
    userInfo: types.maybe(UserInfoModel),
    appVersion: types.optional(types.string, ''),
    lastVisitedTab: types.maybe(types.frozen<LastVisitedTab>()),
    streakCount: types.maybe(types.number),
    streakDays: types.optional(types.array(types.enumeration<Day>([...days])), []),
    isLoggedInFirstTime: types.optional(types.boolean, false),
    userVisitCount: types.optional(types.number, -1),
    isSubscribedToNewletter: types.optional(types.boolean, false),
    showDeleteAccountAlert: types.optional(types.boolean, false),
    isPNSheetOpen: types.optional(types.boolean, false),
    onboardingQuestions: types.maybe(QuestionsResponseModel),
    onboardingProgress: types.maybe(QuestionsProgressModel),
    userEventRating: types.optional(types.number, 0),
    userEmail: types.optional(types.string, ''),
    userPassword: types.optional(types.string, ''),
    isEmailVerified: types.optional(types.boolean, false),
    isEmailPasswordVerified: types.optional(types.boolean, false),
    isMeLoading: types.optional(types.boolean, false),
    isMeFetching: types.optional(types.boolean, false),
    isUserInfoLoaded: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  // .views((self) => ({
  //   get name() {
  //     if (self.firstName && self.lastName) {
  //       return `${self.firstName} ${self.lastName}`
  //     }
  //     return ''
  //   },
  // }))
  .actions((self) => ({
    clearUser() {
      self.user = undefined
      self.rootStore.telemetry.removeUser()
    },
    setUser(data: UserModelType) {
      self.user = data
    },
    setAppVersion(version: string) {
      self.appVersion = version
    },
    setLastVisitedTab(lastVisitedTab: LastVisitedTab) {
      self.lastVisitedTab = lastVisitedTab
    },
    setUserInfo(data: UserInfoModelType) {
      self.userInfo = data
    },
    setStreakCount(value: number | undefined) {
      self.streakCount = value
    },
    setStreakDays(value: Day[]) {
      self.streakDays.replace(value)
    },
    setIsLoggedInFirstTime(value: boolean) {
      self.isLoggedInFirstTime = value
    },
    setUserVisitCount(value: number) {
      self.userVisitCount = value
    },
    setIsSubscribedToNewsletter(value: boolean) {
      self.isSubscribedToNewletter = value
    },
    setShowDeleteAccountAlert(value: boolean) {
      self.showDeleteAccountAlert = value
    },
    setIsPNSheetOpen(value: boolean) {
      self.isPNSheetOpen = value
    },
    setOnboardingQuestions(value: QuestionsResponseModelType) {
      self.onboardingQuestions = value
    },
    setOnboardingProgress(value: QuestionsProgressModelType) {
      self.onboardingProgress = value
    },
    setUserEventRating(value: number) {
      self.userEventRating = value
    },
    setUserEmail(value: string) {
      self.userEmail = value
    },
    setUserPassword(value: string) {
      self.userPassword = value
    },
    setIsEmailVerified(value: boolean) {
      self.isEmailVerified = value
    },
    setIsEmailPasswordVerified(value: boolean) {
      self.isEmailPasswordVerified = value
    },
    setIsMeLoading(value: boolean) {
      self.isMeLoading = value
    },
    setIsMeFetching(value: boolean) {
      self.isMeFetching = value
    },
    setIsUserInfoLoaded(value: boolean) {
      self.isUserInfoLoaded = value
    }
  }))
  .actions((self) => ({
    async fetchMe() {
      if (shouldSetMeLoading(self.user !== undefined)) {
        self.setIsMeLoading(true)
      }
      self.setIsMeFetching(true)
      self.setIsUserInfoLoaded(false)

      try {
        logger.debug('fetchMe')
        const response = await self.rootStore.api.queryMe(
          {},
          (qb) => userModelPrimitives.pushNotificationConfig(userPushNotificationConfigModelPrimitives),
          { fetchPolicy: 'no-cache' }
        )
        logger.debug('fetchMe response', response)
        const me = response.me
        if (me) {
          if (UserModel.is(me)) {
            const user = UserModel.create(me)

            await self.rootStore.session.getUserInfo(user._id)

            await self.rootStore.telemetry.setUser(user)
            self.setUser(user)
            logger.debug('fetchMe user', self.user)
          }
        }
      } catch (error) {
        logger.error('fetchMe', error)
        //@ts-ignore
        if (['User not authorized.', 'Forbidden resource'].includes(error.response.errors[0].message)) {
          self.rootStore.session.logout()
        }
        throw error
      } finally {
        self.setIsMeLoading(false)
        self.setIsMeFetching(false)
      }
    },
    async checkIsUsernameTaken(username: string): Promise<boolean> {
      try {
        const response = await self.rootStore.api.queryCheckUserNameAlreadyExists(
          { name: username },
          { fetchPolicy: 'no-cache' }
        )
        logger.log('checkIsUsernameTaken response', response.checkUserNameAlreadyExists)
        return response.checkUserNameAlreadyExists
      } catch (error) {
        logger.error('checkIsUsernameUnique', error)
        return true
      }
    },
    async updateUserNameOnAppAndWordpress(changedUserName: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId,
            userName: changedUserName
          }
          const response = await self.rootStore.api.mutateUpdateUserNameAndWordpressUserName(payload)
          logger.info('updateUserNameOnAppAndWordpress response', response.updateUserNameAndWordpressUserName)
          await self.rootStore.session.fetchMe()
        }
      } catch (error) {
        logger.error('updateUserNameOnAppAndWordpress', error)
        throw error
      }
    },
    async updateUsername(changedUserName: string) {
      const userId = self.rootStore.session.user?._id.toString()
      if (!userId) return
      try {
        const response = await self.rootStore.api.mutateUpdateUserNameAndWordpressUserName({
          userId,
          userName: changedUserName
        })
        logger.info('updateUsername response', response.updateUserNameAndWordpressUserName)
        self.setUser(response.updateUserNameAndWordpressUserName)
      } catch (error) {
        logger.error('updateUsername', error)
        throw error
      }
    },
    async deleteAccountData(userId: string) {
      if (userId) {
        try {
          await self.rootStore.api.mutateDeletePlayerStats({ id: userId })
          await self.rootStore.api.mutateDeleteUser({ id: userId })
        } catch (error) {
          logger.error('deleteAccountData', error)
          throw error
        } finally {
          logger.debug(`Successfully deleted user with userId: ${userId} and all of their data.`)
          await self.rootStore.game.leaveCurrentGame()
          await self.rootStore.session.logout()
        }
      }
    },
    async fetchOnboardingQuestions(): Promise<QuestionsResponseModelType> {
      try {
        const response = await self.rootStore.api.queryGetQuestionAnswers(
          {},
          'questions { id question subtitle type values footer }',
          {
            fetchPolicy: 'no-cache'
          }
        )

        logger.debug('fetchOnboardingQuestions response', response)

        const filteredQuestions = response.getQuestionAnswers.questions?.filter(
          (q) => q.id && !onboardingQuestionsToFilterById.includes(q.id)
        )

        const payload = {
          ...response.getQuestionAnswers,
          questions: filteredQuestions
        }

        self.setOnboardingQuestions(payload)
        return payload
      } catch (error) {
        logger.error('fetchOnboardingQuestions', error)
        throw error
      }
    },
    async fetchOnboardingProgress(): Promise<QuestionsProgressModelType> {
      try {
        const isTokenExist = useSessionStore.getState().isLoggedIn()
        if (isTokenExist) {
          const response = await self.rootStore.api.queryGetQuestionsProgress({}, 'totalQuestions answeredQuestions', {
            fetchPolicy: 'no-cache'
          })

          type QuestionAnswer = {
            id: number
            subtitle: string
            question: string
            values: []
            type: string
            answers: []
            footer: string
          }

          const questionAnswers: QuestionAnswer[] = self.rootStore.session.userInfo?.questionAnswers ?? []

          const filteredQuestions = questionAnswers.filter((q) => !onboardingQuestionsToFilterById.includes(q.id))

          const answeredQuestions = filteredQuestions.filter((question) => question.answers.length > 0).length || 0

          const answered = answeredQuestions ?? 0
          const total = filteredQuestions.length ?? 0

          logger.debug('fetchOnboardingProgress response', response)

          const progressQuestions = {
            ...response.getQuestionsProgress,
            answeredQuestions: answered,
            totalQuestions: total
          }
          logger.debug('fetchOnboardingProgress progressQuestions', progressQuestions)

          self.setOnboardingProgress(progressQuestions)
          return progressQuestions
        }
        return { totalQuestions: 0, answeredQuestions: 0 } as QuestionsProgressModelType
      } catch (error) {
        logger.error('fetchOnboardingProgress', error)
        throw error
      }
    }
  }))
  .views((self) => ({
    get firstName() {
      const name = (self.user?.name ?? ' ').trim().split(' ')
      return name.length > 0 ? name[0] : ''
    },
    get lastName() {
      const name = (self.user?.name ?? ' ').split(' ')
      return name.length > 0 ? name[1] : ''
    },
    get isTeacher() {
      const roles = self.user?.userRoles ?? []
      return roles.includes(UserRoles.teacher)
    },
    get isAdmin() {
      const roles = self.user?.userRoles ?? []
      return roles.includes(UserRoles.admin)
    },
    get isSuperAdmin() {
      const roles = self.user?.userRoles ?? []
      return roles.includes(UserRoles.superadmin)
    },
    get isHost(): boolean {
      const channelName = self.rootStore.communication.channel ? self.rootStore.communication.channel.hostId : ''
      const userId = self.rootStore.session.user?._id
      return channelName === userId
    },
    get isUserVisitingForFirstTime() {
      const userInfoCreatedDate = self.userInfo?.createdAt ? new Date(self.userInfo?.createdAt) : new Date()
      const todaysDate = new Date()
      if (
        userInfoCreatedDate.getDate() === todaysDate.getDate() &&
        userInfoCreatedDate.getMonth() === todaysDate.getMonth() &&
        userInfoCreatedDate.getFullYear() === todaysDate.getFullYear()
      ) {
        return true
      }
      return false
    },
    get needsCompleteProfile() {
      // Disabled: CompleteYourProfile routes to the old branching onboarding flow
      // (/onboarding/name?isResuming=true), which no longer matches the new linear flow.
      // if (!self.onboardingProgress) return false
      // const answeredQuestions = self.onboardingProgress.answeredQuestions
      // const totalQuestions = self.onboardingProgress.totalQuestions
      // return answeredQuestions !== totalQuestions
      return false
    }
  }))
  .actions((self) => ({
    getUserVisitMsg() {
      const userInfoCreatedDate = self.userInfo?.createdAt ? new Date(self.userInfo?.createdAt) : new Date()
      const todaysDate = new Date()
      const _MS_PER_DAY = 1000 * 60 * 60 * 24
      // Discard the time and time-zone information.
      const createdDateInUtc = Date.UTC(
        userInfoCreatedDate.getFullYear(),
        userInfoCreatedDate.getMonth(),
        userInfoCreatedDate.getDate()
      )
      const todayInUtc = Date.UTC(todaysDate.getFullYear(), todaysDate.getMonth(), todaysDate.getDate())
      const diffInDays = Math.floor((todayInUtc - createdDateInUtc) / _MS_PER_DAY)
      if (self.rootStore.session.isUserVisitingForFirstTime) {
        return FIRST_VISIT_MSG
      }
      if (diffInDays === 1) {
        return SECOND_VISIT_MSG
      }
      const msgIndex = diffInDays % VISIT_MSG.length
      return VISIT_MSG[msgIndex]
    },
    getRolesByEmail(email: string): UserRoles[] {
      // Note: Deprecated removing Peak6 and Apex emails as Teachers
      //  if (email.match(/pokerpowh?er.com/i)?.length ?? false) {
      //    return [UserRoles.user, UserRoles.teacher]
      //  } else if (email.match(/apexfintechsolutions.com/i)?.length ?? false) {
      //    return [UserRoles.user, UserRoles.teacher]
      //  } else if (email.match(/peak6.com/i)?.length ?? false) {
      //    return [UserRoles.user, UserRoles.teacher]
      //  }
      if (email.match(/pokerpowh?er.com/i)?.length ?? false) {
        return [UserRoles.user, UserRoles.teacher]
      }
      return [UserRoles.user]
    },
    async updateUserData(userName?: string, userEmail?: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            id: userId,
            user: {
              name: userName ?? self.user?.name,
              email: userEmail ?? self.user?.email
            }
          }
          const response = await self.rootStore.api.mutateUpdateUser(payload)
          logger.debug('[session-user] updateUserData response', response)
          await self.rootStore.session.fetchMe()
        }
      } catch (error) {
        logger.error('[session-user] updateUserData error', error)
        throw error
      }
    },
    async updateName(username: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            id: userId,
            user: {
              name: username.trim()
            }
          }
          const response = await self.rootStore.api.mutateUpdateUser(payload)
          logger.debug('updateName response', response)
        }
      } catch (error) {
        logger.error('updateName error', error)
        throw error
      }
    },
    async updateUserPushNotificationConfig(pushNotificationConfig: { gamePlay?: boolean; dailyPokerish?: boolean }) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            id: userId,
            user: {
              pushNotificationConfig: {
                ...pushNotificationConfig
              }
            }
          }
          const response = await self.rootStore.api.mutateUpdateUser(payload)
          logger.debug('Update user push config response: ', response)
          self.fetchMe()
        }
      } catch (error) {
        logger.error('Update user push config error: ', error)
        throw error
      }
    },
    async sendNewEmail(email: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const response = await self.rootStore.api.mutateSendVerifyUserEmail({ email })
          logger.debug('Send email response: ', response)
        }
      } catch (error) {
        logger.error('Send email error: ', error)
        throw error
      }
    },
    async verifyUserEmail(code: string, email: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            code,
            email
          }
          const response = await self.rootStore.api.mutateVerifyUserEmail(payload)
          logger.debug('Verify email response: ', response)
        }
      } catch (error) {
        logger.error('Verify email error: ', error)
        throw error
      }
    },
    async deleteAccount() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const response = await self.rootStore.api.mutateDeleteUser({ id: userId })
          logger.debug('Delete account response: ', response)
        }
      } catch (error) {
        logger.error('Delete account error: ', error)
        throw error
      }
    },
    async updateAppVersion() {
      try {
        const appVersion = getAppVersion()
        if (appVersion === self.appVersion) return
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            id: userId,
            user: {
              appVersion
            }
          }
          const response = await self.rootStore.api.mutateUpdateUser(payload)
          self.setAppVersion(appVersion)
          logger.debug('Update app version response: ', response)
        }
      } catch (error) {
        logger.error('Update app version error: ', error)
        throw error
      }
    },
    async updateAppState(state: string) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            id: userId,
            user: {
              appState: state
            }
          }
          const response = await self.rootStore.api.mutateUpdateUser(payload)
          logger.debug('Update app state response: ', response)
        }
      } catch (error) {
        logger.error('Update app state Error: ', error)
        throw error
      }
    },
    async getUserInfo(_userId?: string) {
      try {
        const userId = _userId || self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId
          }

          const selector = () =>
            selectFromUserInfo().userLiveEvent(selectFromLiveEvent().eventName.date)._id.isUserFromLiveEvent
              .myFirstGameFinish.createdAt.isUserNew.questionAnswers.updatedAt.userId.userEventRating

          const response: { getUserInfo: UserInfoModelType } = await self.rootStore.api.queryGetUserInfo(
            payload,
            selector,
            { fetchPolicy: 'no-cache' }
          )
          logger.debug('GetUserInfo: ', response)
          const userInfo = response.getUserInfo
          if (!userInfo) {
            logger.warn('GetUserInfo returned empty response', { userId })
            return undefined
          }
          self.setUserEventRating(userInfo.userEventRating ?? 0)
          self.setUserInfo(userInfo)
          self.setIsUserInfoLoaded(true)
          if (userInfo.userLiveEvent) {
            const eventData = {
              eventName: userInfo.userLiveEvent?.eventName,
              date: userInfo.userLiveEvent?.date
            } as LiveEventModelType
            self.rootStore.monday.setSelectedEventData(eventData)
          }
          return userInfo
        }
      } catch (error) {
        console.error('GetUserInfo Error: ', error)
        throw error
      }
    },
    async updateUserAnswers(questionId: number, answers: string[]) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId,
            questionId,
            answers
          }
          logger.debug('UpdateUserAnswers: payload', payload)
          const response = await self.rootStore.api.mutateUpdateUserAnswers(payload)
          logger.debug('UpdateUserAnswers: response', response)
        }
      } catch (error) {
        logger.error('UpdateUserAnswers Error: ', error)
        throw error
      }
    },
    async updateUserInfo(UserDetails: UserDetails) {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId,
            user: UserDetails
          }
          logger.debug('updateUserInfo: payload', payload)
          const response = await self.rootStore.api.mutateUpdateUserInfo(payload)
          logger.debug('updateUserInfo: response', response)
          self.setUserInfo(response.updateUserInfo)
        }
      } catch (error) {
        logger.error('updateUserInfo Error: ', error)
        throw error
      }
    },
    async getUserStreak() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            userId
          }
          const response: { getUserStreak: UserStreakResponseModelType } = await self.rootStore.api.queryGetUserStreak(
            payload,
            undefined,
            { fetchPolicy: 'no-cache' }
          )

          const streakCount = response.getUserStreak.streakCount
          const streakDays = response.getUserStreak.days as Day[] // TODO: Type UserStreakResponse days.
          self.setStreakCount(streakCount)
          self.setStreakDays(streakDays)
          return response
        }
      } catch (error) {
        logger.error('GetUserStreak Error: ', error)
        throw error
      }
    },
    async clearUserInfo() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId
          }
          await self.rootStore.api.queryRemoveUserInfo(payload, { fetchPolicy: 'no-cache' })
          // self.rootStore.messages.setSnack('Onboarding data cleared')
          // const snackMessage = translate('adminGameScreen.onboardingDataCleared')
          // self.rootStore.messages.setSnack(snackMessage)
          // self.rootStore.messages.toggleSnackbar()
          setTimeout(() => {
            self.rootStore.messages.clearSnack()
            // self.rootStore.messages.toggleSnackbar()
          }, 5000)
        }
      } catch (error) {
        logger.error('removeUserInfo Error: ', error)
        throw error
      }
    },
    async getUserNotification() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          const payload = {
            userId: userId
          }
          logger.debug('getUserNotification: payload', userId)
          const response = await self.rootStore.api.queryGetUserNotification(payload)
          self.rootStore.session.setIsSubscribedToNewsletter(response.getUserNotification)
          logger.debug('getUserNotification: response', response.getUserNotification)
        }
      } catch (error) {
        logger.error('getUserNotification Error ', error)
        throw error
      }
    },
    async subscribeNewsLetter({ optIn }: { optIn: boolean }) {
      try {
        const email = self.rootStore.session.user?.email?.toString()
        const firstName = self.rootStore.session.firstName || ''
        const lastName = self.rootStore.session.lastName || ''

        if (email) {
          const response = await self.rootStore.api.mutateSubscribeUser({ email, firstName, lastName, optIn })
          logger.debug('subscribeNewsLetter: response', response)
        }
      } catch (error) {
        logger.error('subscribeNewsLetter Error: ', error)
        throw error
      }
    },
    async unSubscribeNewsLetter() {
      try {
        const email = self.rootStore.session.user?.email?.toString()
        if (email) {
          const response = await self.rootStore.api.mutateUnsubscribeUser({ email })
          logger.debug('unSubscribeNewsLetter: response', response)
        }
      } catch (error) {
        logger.error('unSubscribeNewsLetter Error: ', error)
        throw error
      }
    },
    async updateUserVisitCount() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        if (userId) {
          logger.debug('updateUserVisitCount: payload', userId)
          const response = await self.rootStore.api.mutateUpdateUserVisitCount({ userId })
          self.rootStore.session.setUserVisitCount(response.updateUserVisitCount.userVisitCount ?? -1)
          logger.debug('updateUserVisitCount: response', response.updateUserVisitCount.userVisitCount)
        }
      } catch (error) {
        logger.error('updateUserVisitCount Error: ', error)
        throw error
      }
    },
    async mergeAnonymousUserId() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const analyticsSegmentId = self.rootStore.analytics.getSegmentUserID()
        const segmentAnonymousId = self.rootStore.share.segmentAnonymousId || analyticsSegmentId
        if (segmentAnonymousId && userId) {
          const payload = {
            segmentAnonymousId,
            userId: userId
          }
          const response = await self.rootStore.api.mutateMergeAnonymousUser(payload)
          logger.debug('mergeAnonymousUserId API payload', payload)
          logger.debug('mergeAnonymousUserId API response', response.mergeAnonymousUser)
        } else {
          logger.debug('mergeAnonymousUserId not found')
        }
      } catch (error) {
        logger.error('mergeAnonymousUserId API error', error)
      }
    }
  }))
  .actions((self) => ({
    async updateUserNameAndAnalytics(userName: string) {
      self.rootStore.analytics.setEvent(events.onboarding.poker_name_created, {
        pokerName: userName,
        segmentUserId: self.rootStore.analytics.getSegmentUserID(),
        segmentIdentity: self.rootStore.analytics.getUserIdentify()
      })
      // No fetchMe here: updateUserNameOnAppAndWordpress already refreshed user + userInfo,
      // and the identify payload below carries the new name explicitly.
      if (self.rootStore.session.user?.email) {
        self.rootStore.session.user &&
          self.rootStore.analytics.setUserIdentify(self.rootStore.session.user._id, {
            name: userName,
            email: self.rootStore.session.user?.email
          })
      }
    }
  }))
  .actions((self) => {
    const afterAttach = async () => {
      logger.debug('afterAttach')
      if (useSessionStore.getState().isLoggedIn()) {
        await self.fetchMe()
      }
      logger.debug('afterAttach me', self.user)
    }
    return { afterAttach }
  })

// .actions((self) => {
//   const root = self.rootStore
//   const getUser = async () => {
//     try {
//       const response: IUserResponse = await root.services.api.session.get('/users')
//       const { data } = response
//       self.setUser(data)
//     } catch (error) {
//       logger.error('unable to getUser', { error })
//     }
//     return self
//   }

//   return { getUser }
// })

/**
 * The SessionUserStore instance.
 */
export type SessionUserStore = Instance<typeof SessionUserStoreModel>

/**
 * The data of a SessionUserStore.
 */
export type SessionUserStoreSnapshot = SnapshotOut<typeof SessionUserStoreModel>
